import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AdminGlobalSearch, type AdminSearchEntry } from "@/components/admin/AdminGlobalSearch";
import { LanguageSelector } from "@/components/ui/language-selector";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useApp } from "@/hooks/use-app-context";
import { useConfirmedLogout } from "@/hooks/use-confirm";
import { saveAuthReturnPath } from "@/lib/auth-return";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { findActiveItem } from "./nav-match";
import { ShellSidebar } from "./ShellSidebar";
import { ShellUserMenu, type ShellAccountLink } from "./ShellUserMenu";
import type { ShellNavSection } from "./types";
import { OfflineStatusButton } from "@/features/offline/components/OfflineStatusButton";

const COLLAPSED_KEY = "yodi-shell-collapsed";

const copy = {
  fr: {
    menu: "Ouvrir le menu",
    account: "Menu du compte",
    collapse: "Réduire le menu",
    expand: "Déplier le menu",
    settings: "Mon profil",
    website: "Site web",
    light: "Mode clair",
    dark: "Mode sombre",
    logout: "Se déconnecter",
    noEmail: "Aucun email",
  },
  en: {
    menu: "Open the menu",
    account: "Account menu",
    collapse: "Collapse the menu",
    expand: "Expand the menu",
    settings: "My profile",
    website: "Website",
    light: "Light mode",
    dark: "Dark mode",
    logout: "Log out",
    noEmail: "No email",
  },
};

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export interface AppShellProps {
  /** Name of the space, shown next to the logo and as the breadcrumb root. */
  title: string;
  /** Where the logo and the breadcrumb root lead. */
  homeHref: string;
  sections: ShellNavSection[];
  /** More pages for the search palette, beyond the menu entries. */
  extraSearchEntries?: AdminSearchEntry[];
  /** Account menu links before the theme switch (settings is always there). */
  accountLinks?: ShellAccountLink[];
  /** Shown above the top bar, e.g. a collector's running session. */
  banner?: ReactNode;
  roleLabel?: string;
  /** The profile page inside this space, so nobody leaves their panel. */
  settingsHref: string;
  children: ReactNode;
}

/**
 * The one layout of the back-office spaces (admin, moderator, accountant,
 * contributor workspace): sidebar built from the space's menu, top bar with
 * breadcrumb, search (⌘K), language and the account menu. Each space only
 * passes its menu; nothing about the chrome is copied per space.
 */
export function AppShell({ title, homeHref, sections, extraSearchEntries = [], accountLinks = [], banner, roleLabel, settingsHref, children }: AppShellProps) {
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang === "en" ? "en" : "fr";
  const text = copy[currentLang];
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, theme, toggleTheme } = useApp();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleSections = useMemo(() => sections.filter((section) => section.items.length > 0), [sections]);
  // The profile is reached from the account menu, not the sidebar: name it in
  // the breadcrumb too.
  const activeItem = findActiveItem(visibleSections, location.pathname, location.search);
  const active = activeItem ?? (location.pathname === settingsHref ? { label: text.settings } : null);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, value ? "0" : "1");
      } catch {
        // Private mode: the choice just isn't remembered.
      }
      return !value;
    });
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "b") return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable=true]")) return;
      event.preventDefault();
      toggleCollapsed();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleCollapsed]);

  // The drawer closes on navigation, whatever triggered it.
  useEffect(() => setMobileOpen(false), [location.pathname, location.search]);

  const searchEntries = useMemo<AdminSearchEntry[]>(
    () => [
      ...visibleSections.flatMap((section) =>
        section.items.map((item) => ({
          title: item.label,
          href: item.href,
          icon: item.icon,
          group: section.title ?? title,
          keywords: item.keywords,
        })),
      ),
      ...extraSearchEntries,
    ],
    [visibleSections, extraSearchEntries, title],
  );

  const logout = useConfirmedLogout(() => {
    saveAuthReturnPath();
    setUser(null);
    navigate(`/${currentLang}/login`);
  });

  const sidebarProps = {
    title,
    homeHref,
    sections: visibleSections,
    activeKey: activeItem?.key,
    collapseLabel: text.collapse,
    expandLabel: text.expand,
  };

  return (
    <div className="relative flex h-screen overflow-hidden bg-background text-foreground">
      <aside className={cn("hidden shrink-0 border-r border-border/60 transition-[width] duration-200 lg:block", collapsed ? "w-16" : "w-60")}>
        <ShellSidebar {...sidebarProps} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 border-border/60 p-0 [&>button]:hidden">
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <ShellSidebar {...sidebarProps} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {banner}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border/60 bg-background/90 px-4 backdrop-blur sm:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label={text.menu}
            className="-ml-1.5 grid size-9 place-items-center rounded-md text-muted-foreground hover:bg-surface-2 hover:text-foreground lg:hidden"
          >
            <Icon name="menu" className="size-5" />
          </button>
          <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
            <Link to={homeHref} className="hidden shrink-0 text-muted-foreground hover:text-foreground sm:inline">
              {title}
            </Link>
            {active ? (
              <>
                <Icon name="chevronRight" className="hidden size-3.5 shrink-0 text-faint-foreground sm:block" />
                <span className="truncate font-medium text-foreground">{active.label}</span>
              </>
            ) : null}
          </nav>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <Link
              to={`/${currentLang}/site`}
              target="_blank"
              rel="noopener"
              className="hidden h-9 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground md:inline-flex"
            >
              <Icon name="externalLink" className="size-4" aria-hidden />
              {text.website}
            </Link>
            <AdminGlobalSearch entries={searchEntries} lang={currentLang} />
            <OfflineStatusButton variant="shell" />
            <LanguageSelector variant="admin" className="hidden md:inline-flex" />
            <ShellUserMenu
              name={user?.username}
              email={user?.email}
              roleLabel={roleLabel}
              links={[
                { label: text.settings, href: settingsHref, icon: "user" },
                ...accountLinks,
                // Each role only has its panel; the public website opens beside it.
                { label: text.website, href: `/${currentLang}/site`, icon: "externalLink", newTab: true },
              ]}
              themeLabel={theme === "dark" ? text.light : text.dark}
              themeIcon={theme === "dark" ? "sun" : "moon"}
              onToggleTheme={toggleTheme}
              logoutLabel={text.logout}
              onLogout={logout}
              noEmailLabel={text.noEmail}
              menuLabel={text.account}
            />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
